import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { Box, Typography } from "@mui/material";
import {
    formatModuleScoreConditions,
    groupModuleScores,
} from "components/ApplicationFilters/FiltersAndChips/ModuleScores/moduleScoreLabels";
import AcxChip from "components/UI/AcxChip";
import { formatSegmentHeading } from "components/UI/AcxTranscription/utils";
import type { SlimLLMClassifierResponse } from "models/LLMs";
import { observer } from "mobx-react";
import moment from "moment";
import React, { useEffect, useMemo } from "react";
import { ApplicationFilterItemsStore } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import type {
    DataLabel,
    LanguageFilterObject,
    ModuleScore,
    PrimaryDriver,
    PrimaryTopic,
    QuestionResultFilter,
    SimpleTheme,
    SimpleTopicCluster,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import type { IRBCFilter } from "components/Conversations/Views/Drawer/components/DeepFilter/RBCFilterStore";
import { Language } from "stores/ApplicationFilters/Filters/Language";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { getQuestionResultLabelParts } from "components/ApplicationFilters/FiltersAndChips/QuestionResult/questionResultLabels";
import { CallDirection } from "stores/ApplicationFilters/Filters/CallDirection";
import { EvaluationType } from "stores/ApplicationFilters/Filters/EvaluationType";
import { HipaaCompliance } from "stores/ApplicationFilters/Filters/HipaaCompliance";
import { MediaType } from "stores/ApplicationFilters/Filters/MediaType";
import { SafetyEventAsString } from "stores/ApplicationFilters/Filters/SafetyEvent";
import { Sentiment } from "stores/ApplicationFilters/Filters/Sentiment";
import theme from "Theme/AppTheme";
import { useStore } from "utils/useStore";
import BaseObjectStore from "../BaseObjectStore";
import {
    getObjectFilterKeysFromFilters,
    isExtendedMetadataObjectFilterKey,
    isFilterValueEmpty,
    OBJECT_CALL_DURATION_FILTER_KEY,
    OBJECT_MESSAGE_COUNT_FILTER_KEY,
} from ".";

type FilterTooltipEntry = {
    label: string;
    value: string;
};

type Props = {
    store: BaseObjectStore<any, any>;
};

/** Formats date reference enum values into human-readable labels. */
function formatDateReferenceOption(value: unknown): string {
    const raw = `${value ?? ""}`;
    if (!raw.length) return "";

    return raw
        .replace(/Date$/g, "")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .trim();
}

/** Formats value arrays for compact display in the tooltip/chip summary. */
function formatList(values: string[], maxItems = 3): string {
    if (values.length <= maxItems) return values.join(", ");
    const shown = values.slice(0, maxItems);
    return `${shown.join(", ")} +${values.length - maxItems} more`;
}

/** Renders a compact indicator chip summarizing committed object-level filters. */
const ObjectFilterIndicator = observer(({ store }: Props) => {
    const applicationFilterItemsStore = useStore(ApplicationFilterItemsStore);
    const applicationFilterValuesStore = useStore(ApplicationFilterValuesStore);
    // The sanitizer drops Question Result rows this viewer cannot see, so the
    // memos below must recompute once the module catalog resolves.
    const questionResultModuleAccessKey =
        applicationFilterValuesStore.questionResultModuleAccessKey;

    const currentObjectFilters =
        store.objectFiltersStore?.toFilterObject() ?? null;
    const sanitizedDisplayObjectFiltersResult = useMemo(
        () => {
            // Read so exhaustive-deps accepts the key below: the real dependency
            // runs through mobx state inside stripInaccessibleFilters, where the
            // linter cannot see it.
            void questionResultModuleAccessKey;

            return currentObjectFilters
                ? applicationFilterItemsStore.stripInaccessibleFilters(
                      currentObjectFilters,
                  )
                : null;
        },
        [
            applicationFilterItemsStore,
            currentObjectFilters,
            questionResultModuleAccessKey,
        ],
    );
    const displayObjectFilters =
        sanitizedDisplayObjectFiltersResult?.filters ?? null;
    const committedObjectFilters = store.getLastAppliedObjectFiltersSnapshot();
    const sanitizedCommittedObjectFiltersResult = useMemo(
        () => {
            // Read so exhaustive-deps accepts the key below: the real dependency
            // runs through mobx state inside stripInaccessibleFilters, where the
            // linter cannot see it.
            void questionResultModuleAccessKey;

            return committedObjectFilters
                ? applicationFilterItemsStore.stripInaccessibleFilters(
                      committedObjectFilters,
                  )
                : null;
        },
        [
            applicationFilterItemsStore,
            committedObjectFilters,
            questionResultModuleAccessKey,
        ],
    );
    const removedInaccessibleFilters =
        sanitizedCommittedObjectFiltersResult?.removedInaccessibleFilters ??
        false;
    const hasUnappliedChanges =
        !store.isRefreshing && store.objectFiltersChangedSinceLastApply();
    const displayDateOverride = store.objectFilterDateOverride;
    const displayDateTypeOverride = store.objectFilterDateTypeOverride;
    const displayCallDurationOverride = store.objectFilterCallDurationOverride;
    const displayQuestionResultModuleIdsKey = (
        displayObjectFilters?.questionResults ?? []
    )
        .map((questionResult) => questionResult.licensedModuleId)
        .join("||");

    useEffect(() => {
        const moduleIds = displayQuestionResultModuleIdsKey
            .split("||")
            .filter((moduleId) => moduleId.length > 0);
        if (moduleIds.length === 0) return;

        applicationFilterValuesStore.loadQuestionResultFilterableModules();
        Array.from(new Set(moduleIds)).forEach((moduleId) =>
            applicationFilterValuesStore.loadModuleDetails(moduleId),
        );
    }, [applicationFilterValuesStore, displayQuestionResultModuleIdsKey]);
    const displayMessageCountOverride = store.objectFilterMessageCountOverride;

    // Tooltip rows are plain strings, so this map qualifies the two identically-titled Theme
    // filters by source. See ApplicationFilterItemsStore.plainTextFilterTitleByKey.
    const filterTitleByKey =
        applicationFilterItemsStore.plainTextFilterTitleByKey;

    const entries = useMemo<FilterTooltipEntry[]>(() => {
        if (!displayObjectFilters) return [];

        const nextEntries: FilterTooltipEntry[] = [];
        /** Adds a formatted tooltip row when the value is non-empty. */
        const addEntry = (label: string, value?: string) => {
            if (!value) return;
            const trimmed = value.trim();
            if (!trimmed.length) return;
            nextEntries.push({
                label,
                value: trimmed,
            });
        };

        if (displayDateOverride) {
            const startDate = displayObjectFilters.startDate;
            const endDate = displayObjectFilters.endDate;
            if (startDate && endDate) {
                const startLabel = moment(
                    startDate as moment.MomentInput,
                ).format("MM/DD/YYYY");
                const endLabel = moment(endDate as moment.MomentInput).format(
                    "MM/DD/YYYY",
                );
                const labelPrefix = displayObjectFilters.dateRangeLabel
                    ? `${displayObjectFilters.dateRangeLabel} `
                    : "";

                addEntry(
                    filterTitleByKey.get("dateRange") ?? "Date Range",
                    `${labelPrefix}${startLabel} - ${endLabel}`,
                );
            }
        }

        if (displayDateTypeOverride) {
            addEntry(
                filterTitleByKey.get("dateReferenceOption") ?? "Date Type",
                formatDateReferenceOption(
                    displayObjectFilters.dateReferenceOption,
                ),
            );
        }

        getObjectFilterKeysFromFilters(displayObjectFilters).forEach((key) => {
            if (isExtendedMetadataObjectFilterKey(key)) {
                const metadataKey = key.replace("extendedMetadata.", "");
                const groupedValue =
                    displayObjectFilters.extendedMetadataGrouped?.[metadataKey];
                const keyLabel = filterTitleByKey.get(key) ?? key;

                if (groupedValue?.values && groupedValue.values.length > 0) {
                    const groupedLabel = groupedValue.contains
                        ? groupedValue.values.join(", ")
                        : `excludes ${groupedValue.values.join(", ")}`;
                    addEntry(keyLabel, groupedLabel);
                    return;
                }

                const rawMetadataValue =
                    displayObjectFilters.extendedMetadata?.[metadataKey];
                if (!isFilterValueEmpty(rawMetadataValue)) {
                    addEntry(keyLabel, `${rawMetadataValue}`);
                }
                return;
            }

            if (key === OBJECT_CALL_DURATION_FILTER_KEY) {
                const minDuration = displayObjectFilters.minCallDuration ?? 0;
                const maxDuration = displayObjectFilters.maxCallDuration ?? 0;
                if (
                    minDuration <= 0 &&
                    maxDuration <= 0 &&
                    !displayCallDurationOverride
                ) {
                    return;
                }

                const maxLabel = maxDuration > 0 ? `${maxDuration}` : "Any";
                addEntry(
                    filterTitleByKey.get("callDuration") ?? "Call Duration",
                    `${minDuration} - ${maxLabel} minutes`,
                );
                return;
            }

            if (key === OBJECT_MESSAGE_COUNT_FILTER_KEY) {
                const minMessageCount =
                    displayObjectFilters.minMessageCount ?? 0;
                const maxMessageCount =
                    displayObjectFilters.maxMessageCount ?? 0;
                if (
                    minMessageCount <= 0 &&
                    maxMessageCount <= 0 &&
                    !displayMessageCountOverride
                ) {
                    return;
                }

                const maxLabel =
                    maxMessageCount > 0 ? `${maxMessageCount}` : "Any";
                addEntry(
                    filterTitleByKey.get(OBJECT_MESSAGE_COUNT_FILTER_KEY) ??
                        "Message Count",
                    `${minMessageCount} - ${maxLabel} messages`,
                );
                return;
            }

            const rawValue = (displayObjectFilters as any)[key];
            if (isFilterValueEmpty(rawValue)) return;

            const keyLabel = filterTitleByKey.get(key) ?? key;

            switch (key) {
                case "eddyEffectStatus":
                    addEntry(keyLabel, rawValue ? "Present" : "Not Present");
                    return;
                case "beginningSentiment":
                case "endingSentiment":
                    addEntry(
                        keyLabel,
                        Sentiment.toString(rawValue as Sentiment) ??
                            `${rawValue}`,
                    );
                    return;
                case "topics": {
                    const topicLabels = (rawValue as SimpleTopicCluster[])
                        .map((topic) => topic.topicLabel)
                        .filter((topic) => !!topic);
                    addEntry(keyLabel, formatList(topicLabels));
                    return;
                }
                case "themes": {
                    const themeLabels = (rawValue as SimpleTheme[])
                        .map((theme) => theme.themeLabel)
                        .filter((theme) => !!theme);
                    addEntry(keyLabel, formatList(themeLabels));
                    return;
                }
                case "primaryTopics": {
                    const topicLabels = (rawValue as PrimaryTopic[])
                        .map((topic) => topic.topicLabel)
                        .filter((topic) => !!topic);
                    addEntry(keyLabel, formatList(topicLabels));
                    return;
                }
                case "drivers": {
                    const driverLabels = (rawValue as PrimaryDriver[])
                        .map((driver) => driver.driverName)
                        .filter((driver) => !!driver);
                    addEntry(keyLabel, formatList(driverLabels));
                    return;
                }
                case "contactTypes": {
                    const contactLabels = (rawValue as string[])
                        .map((contactType) =>
                            applicationFilterValuesStore.getContactTypeLabel(
                                contactType,
                            ),
                        )
                        .filter((label) => !!label);
                    addEntry(keyLabel, formatList(contactLabels));
                    return;
                }
                case "hierarchyIds": {
                    const hierarchyCount = (rawValue as string[]).length;
                    if (!hierarchyCount) return;
                    addEntry(
                        keyLabel,
                        `${hierarchyCount} ${
                            hierarchyCount === 1 ? "Hierarchy" : "Hierarchies"
                        } Selected`,
                    );
                    return;
                }
                case "patientJourneyStages": {
                    const stageLabels = (rawValue as unknown[])
                        .map(
                            (stage) =>
                                store.objectFiltersStore?.journeyStageOptions.find(
                                    (option) => option.value === stage,
                                )?.label ?? `${stage}`,
                        )
                        .filter((label) => !!label);
                    addEntry(keyLabel, formatList(stageLabels));
                    return;
                }
                case "agentIds": {
                    const agentNameById = new Map(
                        (applicationFilterValuesStore.agents ?? []).map(
                            (agent) => [
                                `${agent.id ?? ""}`,
                                agent.fullName?.trim() ||
                                    agent.agentEmail ||
                                    `${agent.id ?? ""}`,
                            ],
                        ),
                    );
                    const agentLabels = (rawValue as string[])
                        .map((agentId) => agentNameById.get(`${agentId}`))
                        .filter((label): label is string => !!label);
                    addEntry(keyLabel, formatList(agentLabels));
                    return;
                }
                case "agentManagerIds": {
                    const managerNameById = new Map(
                        (
                            applicationFilterValuesStore.agentManagers ?? []
                        ).map((manager) => [
                            `${manager.id ?? ""}`,
                            manager.fullName?.trim() ||
                                `${manager.id ?? ""}`,
                        ]),
                    );
                    const managerLabels = (rawValue as string[])
                        .map((managerId) => managerNameById.get(`${managerId}`))
                        .filter((label): label is string => !!label);
                    addEntry(keyLabel, formatList(managerLabels));
                    return;
                }
                case "mediaTypes": {
                    const mediaTypeLabels = (rawValue as MediaType[])
                        .map((mediaType) => MediaType.toString(mediaType))
                        .filter((label): label is string => !!label);
                    addEntry(keyLabel, formatList(mediaTypeLabels));
                    return;
                }
                case "callDirections": {
                    const callDirectionLabels = (rawValue as CallDirection[])
                        .map((direction) => CallDirection.toString(direction))
                        .filter((label): label is string => !!label);
                    addEntry(keyLabel, formatList(callDirectionLabels));
                    return;
                }
                case "evaluationTypes": {
                    const evaluationTypeLabels = (rawValue as EvaluationType[])
                        .map((evaluationType) =>
                            EvaluationType.toString(evaluationType),
                        )
                        .filter((label): label is string => !!label);
                    addEntry(keyLabel, formatList(evaluationTypeLabels));
                    return;
                }
                case "favoriteConversations":
                    addEntry(
                        keyLabel,
                        rawValue ? "Show favorite conversations only" : "Off",
                    );
                    return;
                case "safetyEvent":
                    addEntry(keyLabel, SafetyEventAsString(rawValue as any));
                    return;
                case "hipaaCompliance":
                    addEntry(
                        keyLabel,
                        HipaaCompliance.toString(rawValue as HipaaCompliance) ??
                            `${rawValue}`,
                    );
                    return;
                case "modelResults": {
                    const modelResultLabels = (
                        rawValue as SlimLLMClassifierResponse[]
                    ).map(
                        (modelResult) =>
                            `${modelResult.name}: ${formatList(
                                modelResult.results ?? [],
                                2,
                            )}`,
                    );

                    addEntry(keyLabel, formatList(modelResultLabels, 2));
                    return;
                }
                case "outlineSegmentFilterItems": {
                    const outlineLabels = (
                        rawValue as {
                            value?: boolean;
                            segments?: string[];
                        }[]
                    ).map((entry) => {
                        const segments = (entry.segments ?? []).map((segment) =>
                            formatSegmentHeading(segment),
                        );
                        const prefix =
                            entry.value === false ? "Does not contain " : "";

                        return `${prefix}${formatList(segments, 2)}`.trim();
                    });

                    addEntry(keyLabel, formatList(outlineLabels, 2));
                    return;
                }
                case "moduleScores": {
                    // One label per module, its conditions ANDed, matching the chips.
                    const moduleLabels = groupModuleScores(
                        rawValue as ModuleScore[],
                    ).map((group) => {
                        const moduleName =
                            applicationFilterValuesStore.hierarchyLicensedModules?.find(
                                (module) =>
                                    module.id === group.licensedModuleId,
                            )?.title ?? group.licensedModuleId;

                        return `${moduleName} ${formatModuleScoreConditions(
                            group.conditions.map((condition) => condition.entry),
                        )}`.trim();
                    });

                    addEntry(keyLabel, formatList(moduleLabels, 2));
                    return;
                }
                case "questionResults": {
                    const questionResultLabels = (
                        rawValue as QuestionResultFilter[]
                    ).map((questionResult) => {
                        const { moduleLabel, questionLabel, tagLabels } =
                            getQuestionResultLabelParts(
                                questionResult,
                                applicationFilterValuesStore,
                            );

                        return `${moduleLabel} -> ${questionLabel}: ${formatList(
                            tagLabels,
                        )}`;
                    });

                    addEntry(keyLabel, formatList(questionResultLabels, 2));
                    return;
                }
                case "languages": {
                    const langOptions =
                        store.objectFiltersStore?.languageOptions ?? [];
                    const languageLabels = (rawValue as LanguageFilterObject[])
                        .map((lang) => {
                            const option = langOptions.find(
                                (o) => o.value === lang.languageValue,
                            );
                            return (
                                option?.label ??
                                Language[lang.languageValue] ??
                                `${lang.languageValue}`
                            );
                        })
                        .filter((label) => !!label);
                    addEntry(keyLabel, formatList(languageLabels));
                    return;
                }
                case "rbcFilterItems": {
                    const classifierNameById = new Map(
                        (
                            applicationFilterValuesStore.userClassifiers ?? []
                        ).map((c) => [c.id, c.name]),
                    );
                    const rbcLabels = (rawValue as IRBCFilter[])
                        .map((rbc) => {
                            const names = rbc.classifierIds
                                .map((id) => classifierNameById.get(id) ?? id)
                                .filter((name) => !!name);
                            if (!names.length) return null;
                            const prefix =
                                rbc.value === false ? "Does not contain " : "";
                            return `${prefix}${formatList(names, 2)}`.trim();
                        })
                        .filter((label): label is string => !!label);
                    addEntry(keyLabel, formatList(rbcLabels, 2));
                    return;
                }
                case "dataLabels": {
                    const dataLabelNames = (rawValue as DataLabel[])
                        .map((label) => label.name)
                        .filter((name) => !!name);
                    addEntry(keyLabel, formatList(dataLabelNames));
                    return;
                }
                default:
                    addEntry(keyLabel, `${rawValue}`);
                    return;
            }
        });

        return nextEntries;
    }, [
        applicationFilterValuesStore,
        displayCallDurationOverride,
        displayDateOverride,
        displayDateTypeOverride,
        displayMessageCountOverride,
        displayObjectFilters,
        filterTitleByKey,
        store.objectFiltersStore?.journeyStageOptions,
        store.objectFiltersStore?.languageOptions,
    ]);

    if (
        !store.hasCommittedObjectFilters ||
        (entries.length === 0 && !removedInaccessibleFilters)
    ) {
        return null;
    }

    const tooltipTitle =
        entries.length > 0
            ? `${entries.length} Filter${entries.length === 1 ? "" : "s"} ${
                  hasUnappliedChanges ? "Not Applied" : "Applied"
              }`
            : hasUnappliedChanges
            ? "Filters Not Applied"
            : "No Filters Applied";
    const iconColor = hasUnappliedChanges
        ? theme.palette.gray.main
        : theme.palette.primary[500];
    const backgroundColor = hasUnappliedChanges
        ? theme.palette.gray.background
        : theme.palette.primary[100];

    return (
        <AcxChip
            id={`${store.id}-object-filter-indicator`}
            size="small"
            color="lightBlue"
            customTooltip={
                <Box>
                    <Typography
                        sx={{
                            color: theme.palette.white.main,
                            fontWeight: 600,
                            fontSize: "16px",
                            lineHeight: "24px",
                            mb: 0.5,
                        }}
                    >
                        {tooltipTitle}
                    </Typography>
                    {entries.map((entry, index) => (
                        <Typography
                            key={`${entry.label}-${index}`}
                            sx={{
                                color: theme.palette.white.main,
                                fontSize: "14px",
                                lineHeight: "20px",
                                display: "block",
                            }}
                        >
                            {entry.label}: {entry.value}
                        </Typography>
                    ))}
                    {removedInaccessibleFilters && (
                        <Typography
                            sx={{
                                color: theme.palette.white.main,
                                fontSize: "14px",
                                lineHeight: "20px",
                                display: "block",
                                mt: entries.length > 0 ? 0.5 : 0,
                            }}
                        >
                            Some filters are not applied, due to your
                            permissions.
                        </Typography>
                    )}
                </Box>
            }
            label={
                <FilterAltIcon
                    sx={{
                        color: iconColor,
                        fontSize: 16,
                    }}
                />
            }
            sx={{
                minWidth: "32px",
                width: "32px",
                height: "32px",
                padding: 0,
                marginBottom: 0,
                backgroundColor,
                color: iconColor,
                "&:hover": {
                    backgroundColor,
                    color: iconColor,
                },
                "& .MuiChip-label": {
                    width: "100%",
                    height: "100%",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                },
            }}
        />
    );
});

export default ObjectFilterIndicator;
